import React from 'react';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { IconWrap } from '../components/IconWrap';
import { sectors } from './Home/data/sectors';
import {
    CiGlobe,
    CiSettings,
    CiBatteryFull,
    CiTrash,
    CiDroplet,
    CiShoppingCart,
    CiRead,
    CiFileOn,
    CiViewList,
} from 'react-icons/ci';
import useTheme from '../hooks/useTheme';

export const About: React.FC = () => {
    const { theme } = useTheme();

    const sectorIcons: Record<string, React.ReactNode> = {
        'Environmental': <CiGlobe />,
        'Infrastructure': <CiSettings />,
        'Industrial Products & Services': <CiSettings />,
        'Shipbuilding': <CiSettings />,
        'Automotive': <CiSettings />,
        'Electric & Electronics': <CiBatteryFull />,
        'Waste Management': <CiTrash />,
        'Water & Wastewater': <CiDroplet />,
        'Oil & Gas': <CiDroplet />,
        'Pipelines': <CiSettings />,
        'Consumer Goods': <CiShoppingCart />,
    };

    return (
        <>
            <PageHero
                title="About BCBH"
                subtitle="A Korea–Egypt bridge for knowledge exchange, partnerships, and project opportunities."
                breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'About' }]}
            />

            <Section>
                <h2
                    style={{
                        fontSize: '34px',
                        fontWeight: 700,
                        color: 'var(--text)',
                        marginBottom: '32px',
                    }}
                    className="sectionTitle"
                >
                    How it was created
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '48px',
                        marginBottom: '48px',
                    }}
                    className="aboutGrid"
                >
                    <div>
                        <h3
                            style={{
                                fontSize: '22px',
                                fontWeight: 600,
                                color: 'var(--text)',
                                marginBottom: '24px',
                            }}
                        >
                            Timeline
                        </h3>
                        <div
                            style={{
                                position: 'relative',
                                paddingLeft: '24px',
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    left: '8px',
                                    top: '0',
                                    bottom: '0',
                                    width: '2px',
                                    backgroundColor: 'var(--g1)',
                                }}
                            />
                            <div
                                style={{
                                    position: 'relative',
                                    paddingBottom: '32px',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '4px',
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: theme.colors.green.g1,
                                        border: `3px solid ${theme.background.panel}`,
                                    }}
                                />
                                <div
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: theme.colors.green.g1,
                                        marginBottom: '4px',
                                    }}
                                >
                                    26 Mar 2025
                                </div>
                                <div
                                    style={{
                                        fontSize: '16px',
                                        color: theme.text.primary,
                                        marginBottom: '8px',
                                    }}
                                >
                                    MoU Effective Date
                                </div>
                                <div
                                    style={{
                                        fontSize: '14px',
                                        color: theme.text.muted,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Launch of BUSAN–CAIRO BUSINESS HUB (BCBH)
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3
                            style={{
                                fontSize: '22px',
                                fontWeight: 600,
                                color: 'var(--text)',
                                marginBottom: '24px',
                            }}
                        >
                            MoU Key Points
                        </h3>
                        <ul
                            style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}
                        >
                            {[
                                'Launch and organize BCBH (BTEC + LOHMANN)',
                                'Regular business tie-ups between companies in Korea and Egypt',
                                'Know-how exchange; training; JV tender cooperation',
                                'Offices in Cairo and Busan',
                                'Regular meetings, virtual or physical',
                                'BTP supports facilities in Busan',
                            ].map((point, idx) => (
                                <li
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        color: theme.text.primary,
                                        fontSize: '16px',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    <span
                                        style={{
                                            color: theme.colors.green.g1,
                                            marginTop: '4px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        ✓
                                    </span>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            <Section>
                <h2
                    style={{
                        fontSize: '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                        textAlign: 'center',
                    }}
                    className="sectionTitle"
                >
                    Purpose & Scope
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '24px',
                    }}
                    className="purposeScopeGrid"
                >
                    {[
                        {
                            title: 'Know-how Exchange',
                            icon: <CiGlobe />,
                            bullets: [
                                'Technical knowledge transfer',
                                'Best practices sharing',
                                'Innovation partnerships',
                            ],
                        },
                        {
                            title: 'Training & Capacity Building',
                            icon: <CiRead />,
                            bullets: [
                                'Skills development programs',
                                'Technology workshops',
                                'Cross-cultural training',
                            ],
                        },
                        {
                            title: 'Project/Tender Cooperation',
                            icon: <CiViewList />,
                            bullets: [
                                'Joint venture formation',
                                'Tender collaboration',
                                'Project execution support',
                            ],
                        },
                    ].map((item, idx) => (
                        <Card key={idx} hoverable>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '16px',
                                }}
                            >
                                <IconWrap icon={item.icon} size={32} color="primary" />
                                <h3
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: 600,
                                        color: theme.text.primary,
                                    }}
                                >
                                    {item.title}
                                </h3>
                            </div>
                            <ul
                                style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                }}
                            >
                                {item.bullets.map((bullet, bulletIdx) => (
                                    <li
                                        key={bulletIdx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            fontSize: '14px',
                                            color: theme.text.muted,
                                        }}
                                    >
                                        <span style={{ color: theme.colors.green.g1, marginTop: '4px', flexShrink: 0 }}>•</span>
                                        {bullet}
                                    </li>
                                ))}
                            </ul>
                            <p
                                style={{
                                    fontSize: '12px',
                                    color: theme.text.muted,
                                    marginTop: '16px',
                                    fontStyle: 'italic',
                                }}
                            >
                                Aligned with the MoU scope
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section style={{ backgroundColor: theme.background.panel }}>
                <h2
                    style={{
                        fontSize: '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                        textAlign: 'center',
                    }}
                    className="sectionTitle"
                >
                    Operating Model (Preview)
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '24px',
                    }}
                    className="operatingModelGrid"
                >
                    {[
                        {
                            step: 1,
                            title: 'Submit opportunity',
                            status: 'COMING SOON' as const,
                            icon: <CiFileOn />,
                            description: 'Companies submit opportunity briefs through the portal',
                        },
                        {
                            step: 2,
                            title: 'Screening & categorization',
                            status: 'COMING SOON' as const,
                            icon: <CiSettings />,
                            description: 'BCBH team reviews and categorizes submissions',
                        },
                        {
                            step: 3,
                            title: 'Matchmaking & introductions',
                            status: 'COMING SOON' as const,
                            icon: <CiGlobe />,
                            description: 'Potential partners are identified and introduced',
                        },
                        {
                            step: 4,
                            title: 'Meetings / missions / project support',
                            status: 'LIVE' as const,
                            icon: <CiRead />,
                            description: 'Meetings arranged and project support provided',
                        },
                    ].map((item) => (
                        <Card key={item.step} hoverable style={{ textAlign: 'center' }}>
                            <div
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    backgroundColor: item.status === 'LIVE' ? theme.colors.green.g1 : theme.background.panel,
                                    color: item.status === 'LIVE' ? theme.text.primary : theme.text.muted,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    margin: '0 auto 16px',
                                    border: item.status === 'LIVE' ? 'none' : `2px solid ${theme.colors.line}`,
                                }}
                            >
                                {item.step}
                            </div>
                            <Badge status={item.status} />
                            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                                <IconWrap icon={item.icon} size={24} color="primary" />
                            </div>
                            <h3
                                style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                {item.title}
                            </h3>
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: theme.text.muted,
                                    lineHeight: 1.5,
                                }}
                            >
                                {item.description}
                            </p>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section>
                <h2
                    style={{
                        fontSize: '34px',
                        fontWeight: 700,
                        color: 'var(--text)',
                        marginBottom: '32px',
                        textAlign: 'center',
                    }}
                    className="sectionTitle"
                >
                    Sectors We Cover
                </h2>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '16px',
                    }}
                    className="sectorsGrid"
                >
                    {sectors.map((sector) => (
                        <Card
                            key={sector}
                            hoverable
                            style={{
                                textAlign: 'center',
                                padding: '20px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '32px',
                                    color: theme.colors.green.g1,
                                }}
                            >
                                {sectorIcons[sector]}
                            </div>
                            <div
                                style={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: theme.text.primary,
                                }}
                            >
                                {sector}
                            </div>
                        </Card>
                    ))}
                </div>
            </Section>
        </>
    );
};

